'use client';

import { __assign } from '../_virtual/_tslib.js';
import { useMemo, useState, useEffect } from 'react';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect.js';
import useLatestRef from './useLatestRef.js';

var DEFAULT_DURATION = 250;
function camelCaseToKebabCase(str) {
    return str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, function ($, ofs) { return (ofs ? '-' : '') + $.toLowerCase(); });
}
function execWithArgsOrReturn(valueOrFunc, args) {
    return typeof valueOrFunc === 'function' ? valueOrFunc(args) : valueOrFunc;
}
function isEmptyObject(obj) {
    return Object.keys(obj).length === 0;
}
function useDelayUnmount(open, duration) {
    var _a = useState(open), isMounted = _a[0], setMounted = _a[1];
    if (open && !isMounted) {
        setMounted(true);
    }
    useEffect(function () {
        if (!open) {
            var timeout_1 = setTimeout(function () { return setMounted(false); }, duration);
            return function () {
                clearTimeout(timeout_1);
            };
        }
        return function () { };
    }, [open, duration]);
    return isMounted;
}
function useTransitionStatus(open, duration) {
    var _a = useState('initial'), status = _a[0], setStatus = _a[1];
    var _b = useState(false), initiated = _b[0], setInitiated = _b[1];
    var isNumberDuration = typeof duration === 'number';
    var enterDuration = isNumberDuration ? duration : duration.enter || 0;
    var leaveDuration = isNumberDuration ? duration : duration.leave || 0;
    var isMounted = useDelayUnmount(open, leaveDuration);
    useIsomorphicLayoutEffect(function () {
        if (initiated && !isMounted) {
            setStatus('unmount');
        }
    }, [isMounted, initiated]);
    useIsomorphicLayoutEffect(function () {
        if (!isMounted)
            return;
        if (open) {
            setStatus('initial');
            var timeout_2;
            var frame_1 = requestAnimationFrame(function () {
                setStatus('enter');
                timeout_2 = setTimeout(function () { return setStatus('entered'); }, enterDuration);
            });
            return function () {
                cancelAnimationFrame(frame_1);
                clearTimeout(timeout_2);
            };
        }
        setInitiated(true);
        setStatus('before-leave');
        var leaveFrame = requestAnimationFrame(function () { return setStatus('leave'); });
        return function () {
            cancelAnimationFrame(leaveFrame);
        };
    }, [open, isMounted, enterDuration]);
    return {
        isMounted: isMounted,
        status: status
    };
}
function useTransition(context, transitions) {
    var open = context.open, args = context.args;
    var _a = transitions.duration, duration = _a === void 0 ? DEFAULT_DURATION : _a, _b = transitions.initial, initial = _b === void 0 ? { opacity: 0 } : _b, _c = transitions.enter, enter = _c === void 0 ? {} : _c, _d = transitions.entered, entered = _d === void 0 ? {} : _d, _e = transitions.beforeLeave, beforeLeave = _e === void 0 ? {} : _e, _f = transitions.leave, leave = _f === void 0 ? {} : _f;
    var funcArgs = useMemo(function () { return args; }, [JSON.stringify(args)]);
    var _g = useTransitionStatus(open, duration), isMounted = _g.isMounted, status = _g.status;
    var _h = useState(__assign({}, execWithArgsOrReturn(initial, funcArgs))), styles = _h[0], setStyles = _h[1];
    var initialRef = useLatestRef(initial);
    var enterRef = useLatestRef(enter);
    var enteredRef = useLatestRef(entered);
    var beforeLeaveRef = useLatestRef(beforeLeave);
    var leaveRef = useLatestRef(leave);
    var isNumberDuration = typeof duration === 'number';
    var enterDuration = isNumberDuration ? duration : duration.enter || 0;
    var leaveDuration = isNumberDuration ? duration : duration.leave || 0;
    useIsomorphicLayoutEffect(function () {
        var initialStyles = execWithArgsOrReturn(initialRef.current, funcArgs);
        var enterStyles = execWithArgsOrReturn(enterRef.current, funcArgs);
        var enteredStyles = execWithArgsOrReturn(enteredRef.current, funcArgs);
        var beforeLeaveStyles = execWithArgsOrReturn(beforeLeaveRef.current, funcArgs);
        var leaveStyles = execWithArgsOrReturn(leaveRef.current, funcArgs);
        if (status === 'initial') {
            setStyles(function (styles) { return (__assign({ transitionProperty: styles.transitionProperty }, initialStyles)); });
        }
        if (status === 'enter') {
            setStyles(__assign({ transitionDuration: "".concat(enterDuration, "ms"), transitionProperty: Object.keys(enterStyles).map(camelCaseToKebabCase).join(',') }, enterStyles));
        }
        if (status === 'entered') {
            var styles_1 = isEmptyObject(enteredStyles) ? enterStyles : enteredStyles;
            setStyles(__assign({ transitionDuration: "".concat(enterDuration, "ms"), transitionProperty: Object.keys(enterStyles).map(camelCaseToKebabCase).join(',') }, styles_1));
        }
        if (status === 'before-leave') {
            var styles_2 = isEmptyObject(beforeLeaveRef) ? initialStyles : beforeLeaveStyles;
            setStyles(__assign({ transitionDuration: "".concat(leaveDuration, "ms"), transitionProperty: Object.keys(enterStyles).map(camelCaseToKebabCase).join(',') }, styles_2));
        }
        if (status === 'leave') {
            var styles_3 = isEmptyObject(leaveStyles) ? initialStyles : leaveStyles;
            setStyles(__assign({ transitionDuration: "".concat(leaveDuration, "ms"), transitionProperty: Object.keys(enterStyles).map(camelCaseToKebabCase).join(',') }, styles_3));
        }
    }, [initialRef, enterRef, enteredRef, beforeLeaveRef, leaveRef, status, enterDuration, leaveDuration, funcArgs]);
    return { isMounted: isMounted, styles: styles };
}

export { useTransition as default };
//# sourceMappingURL=useTransition.js.map
